import * as THREE from 'three';
import { random } from '../look/noise.js';

/**
 * Butterflies over a patch of flowers (part 2's meadow, 29 Sep 2026): a
 * dozen pairs of wings, drawn half as big again as life so a pupil on the
 * stage sees them flutter, one instanced mesh. Each wanders its own loop
 * over the patch, dipping to the flowers; its wings beat in the vertex
 * shader. `level` (a feature's, 0–1) brings them in or sends them off.
 */

/** Two wings hinged on the body along z, 12 cm across; `wing` is 0 at the body, ±1 at the tips. */
export function butterflyGeometry(): THREE.BufferGeometry {
  // prettier-ignore
  const positions = [
    0, 0, -0.03, -0.06, 0, -0.05, -0.055, 0, 0.035, 0, 0, 0.03,  // left wing
    0, 0, -0.03, 0.06, 0, -0.05, 0.055, 0, 0.035, 0, 0, 0.03,    // right wing
  ];
  const wing = [0, -1, -1, 0, 0, 1, 1, 0];
  const index = [0, 2, 1, 0, 3, 2, 4, 5, 6, 4, 6, 7];
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('wing', new THREE.Float32BufferAttribute(wing, 1));
  geometry.setAttribute(
    'normal',
    new THREE.Float32BufferAttribute(Array.from({ length: 8 }, () => [0, 1, 0]).flat(), 3),
  );
  geometry.setIndex(index);
  return geometry;
}

export type Flutter = {
  /** The patch's middle on the ground, and how far they roam from it. */
  readonly centre: readonly [number, number, number];
  readonly radius: number;
  readonly count: number;
  readonly colours: readonly THREE.ColorRepresentation[];
  readonly seed: number;
  /** Metres over the ground they roam between; 0.2–1.6 by default. */
  readonly height?: readonly [number, number];
  /** How fast time goes for them; 1 by default. */
  readonly speed?: number;
  /** How big, as a multiple of life; 1.5 by default. */
  readonly size?: number;
};

/** Where butterfly `i` is at `t` seconds: a wandering loop over the patch, 0.3–1.6 m up. */
export function butterflyAt(
  flutter: Flutter,
  i: number,
  t: number,
  into = new THREE.Vector3(),
): THREE.Vector3 {
  const p = i * 1.618;
  const [low, high] = flutter.height ?? [0.2, 1.6];
  const mid = (low + high) / 2;
  const half = (high - low) / 2;
  t *= flutter.speed ?? 1;
  const a = t * (0.35 + 0.1 * Math.sin(p)) + p * 2.4;
  const r = flutter.radius * (0.45 + 0.35 * Math.sin(t * 0.23 + p) + 0.15 * Math.sin(t * 1.3 + p));
  return into.set(
    flutter.centre[0] + Math.cos(a) * r,
    flutter.centre[1] +
      mid +
      half * ((0.55 / 0.7) * Math.sin(t * 0.9 + p * 3) + (0.15 / 0.7) * Math.sin(t * 5.3 + p)),
    flutter.centre[2] + Math.sin(a) * r * (0.8 + 0.2 * Math.sin(t * 0.5 + p)),
  );
}

export function buildButterflies(
  flutter: Flutter,
  clock: THREE.IUniform<number>,
): { mesh: THREE.InstancedMesh; update(elapsed: number, level: number): void } {
  const material = new THREE.MeshLambertMaterial({ side: THREE.DoubleSide });
  material.onBeforeCompile = (shader) => {
    shader.uniforms['uClock'] = clock;
    shader.vertexShader =
      'attribute float wing;\nuniform float uClock;\n' +
      shader.vertexShader.replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        float flap = sin(uClock * 17.0 + float(gl_InstanceID) * 2.3);
        // Up and down about the body: a tip rises and falls, the hinge stays.
        transformed.y += abs(wing) * 0.05 * flap;
        transformed.x *= 0.55 + 0.45 * abs(flap);`,
      );
  };
  material.customProgramCacheKey = () => 'butterfly';
  const mesh = new THREE.InstancedMesh(butterflyGeometry(), material, flutter.count);
  mesh.name = 'butterflies';
  mesh.frustumCulled = false;
  const next = random(flutter.seed);
  const colour = new THREE.Color();
  for (let i = 0; i < flutter.count; i++) {
    mesh.setColorAt(i, colour.set(flutter.colours[Math.floor(next() * flutter.colours.length)]!));
  }
  const matrix = new THREE.Matrix4();
  const position = new THREE.Vector3();
  const ahead = new THREE.Vector3();
  const q = new THREE.Quaternion();
  const e = new THREE.Euler();
  // Half as big again as life: a 12 cm butterfly at 11 m is still a butterfly.
  const size = new THREE.Vector3();
  const update = (elapsed: number, level: number): void => {
    mesh.visible = level > 0.01;
    if (!mesh.visible) return;
    for (let i = 0; i < flutter.count; i++) {
      butterflyAt(flutter, i, elapsed, position);
      butterflyAt(flutter, i, elapsed + 0.1, ahead);
      const yaw = Math.atan2(-(ahead.x - position.x), -(ahead.z - position.z));
      q.setFromEuler(e.set(0, yaw, 0.2 * Math.sin(elapsed * 3 + i)));
      // Gone: flown up and away as the level falls.
      position.y += (1 - level) * 6;
      size.setScalar((flutter.size ?? 1.5) * Math.min(1, level * 1.5));
      mesh.setMatrixAt(i, matrix.compose(position, q, size));
    }
    mesh.instanceMatrix.needsUpdate = true;
  };
  update(0, 1);
  return { mesh, update };
}
